import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { NetworkChip } from '@mdvip/shared/components/blocks/NetworkChip';
import { cn } from '@mdvip/shared/lib/utils';
import { useTranslation } from 'react-i18next';
import { type AccountDisplay, getAccountDisplay } from '@/lib/payments/accountDisplay.format';

interface AccountCellProps {
  /** Flat account fields, shared verbatim by `PaymentTransaction` and `UpcomingPayment`. */
  account: Parameters<typeof getAccountDisplay>[0];
  className?: string;
}

/** Brand chip for cards and banks; a muted generic chip for check/removed/unknown profiles. */
function AccountChip({ display }: { display: AccountDisplay }) {
  if (display.kind === 'card') return <NetworkChip network={display.network} />;
  if (display.kind === 'bank') return <NetworkChip kind="bank" />;
  return (
    <span className="flex h-8 w-12 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
      <Icon name="creditCard" size="sm" />
    </span>
  );
}

/**
 * The account column shared by every members-app payment table: chip plus the account's masked
 * detail, with a secondary line (a card's expiration) that sits beneath on narrow viewports and
 * on the same baseline from `sm` up.
 */
export function AccountCell({ account, className }: AccountCellProps) {
  const { t } = useTranslation('members');
  const display = getAccountDisplay(account);

  const primary =
    display.kind === 'card'
      ? display.maskedNumber
      : display.kind === 'bank'
        ? display.label
        : t(`payments.account.${display.labelKey}`);
  const secondary =
    display.kind === 'card' && display.expiry ? `${t('payments.account.expPrefix')} ${display.expiry}` : null;

  return (
    <div className={cn('flex min-w-0 items-center gap-2.5', className)}>
      <AccountChip display={display} />
      <span className="flex min-w-0 flex-col items-start gap-0 sm:flex-row sm:items-baseline sm:gap-1.5">
        <span className="truncate text-sm text-foreground">{primary}</span>
        {secondary && <span className="shrink-0 text-xs text-muted-foreground">{secondary}</span>}
      </span>
    </div>
  );
}
